import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useModuleProgress from '../hooks/useModuleProgress';
import '../styles/pages/_module-pages.scss';
import '../styles/pages/_learn-assessments.scss';
import '../styles/pages/_quiz-completion.scss';
import '../styles/pages/_quiz.scss';

interface QuizQuestion {
  question: string;
  options: string[];
  correctAnswer: number;
  explanation: string;
}

const quizQuestions: QuizQuestion[] = [
  {
    question: "Which of the following is NOT a component of active listening?",
    options: [
      "Paying full attention to the speaker",
      "Using nonverbal cues like nodding",
      "Interrupting to share your own experiences",
      "Providing reflective feedback"
    ],
    correctAnswer: 2,
    explanation: "Interrupting to share your own experiences is not part of active listening. Active listening involves paying attention, showing you're listening, providing feedback, deferring judgment, and responding appropriately."
  },
  {
    question: "What is the main difference between an 'I' statement and a 'You' statement?",
    options: [
      "'I' statements are shorter",
      "'I' statements focus on your feelings while 'You' statements blame others",
      "'You' statements are more polite",
      "There is no real difference"
    ],
    correctAnswer: 1,
    explanation: "'I' statements focus on your own feelings and needs (e.g., 'I feel unheard when...'), while 'You' statements tend to blame others (e.g., 'You never listen to me')."
  },
  {
    question: "Which strategy is most effective for overcoming physical distance in relationships?",
    options: [
      "Only texting occasionally",
      "Regular video calls and planned visits",
      "Ignoring the distance",
      "Ending the relationship"
    ],
    correctAnswer: 1,
    explanation: "Regular video calls, written notes, and planned visits are effective strategies for maintaining relationships despite physical distance."
  },
  {
    question: "What is the trust-empathy cycle?",
    options: [
      "A cycle where trust destroys empathy",
      "A cycle where empathy builds trust and trust enables deeper empathy",
      "A cycle that prevents relationship growth",
      "A cycle that only works in professional relationships"
    ],
    correctAnswer: 1,
    explanation: "The trust-empathy cycle is positive: empathy helps build trust, and trust enables you to be more empathetic, creating stronger, more resilient relationships."
  },
  {
    question: "When building new connections, what should you focus on initially?",
    options: [
      "Sharing your entire life story",
      "Finding shared interests and asking open-ended questions",
      "Criticizing their opinions",
      "Talking about controversial topics"
    ],
    correctAnswer: 1,
    explanation: "When building new connections, start with shared interests, ask open-ended questions, listen more than you talk, and follow up on conversations to establish rapport."
  }
];

const SocioRelationalModule4QuizPage: React.FC = () => {
  const navigate = useNavigate();
  const { currentStep, setCurrentStep, isLoading, saveProgress } = useModuleProgress('socioRelationalM4');
  const [currentQuestion, setCurrentQuestion] = useState<number>(0);
  const [selectedAnswer, setSelectedAnswer] = useState<number | null>(null);
  const [showExplanation, setShowExplanation] = useState(false);
  const [responses, setResponses] = useState<{[key: number]: number}>({});
  const [showCompletion, setShowCompletion] = useState(false);

  const handleAnswerSelect = (answerIndex: number) => {
    if (responses[currentQuestion] === undefined) {
      setSelectedAnswer(answerIndex);
      setShowExplanation(true);
      setResponses(prev => ({...prev, [currentQuestion]: answerIndex}));
    }
  };

  const handleNext = () => {
    if (currentQuestion < quizQuestions.length - 1) {
      const nextQuestion = currentQuestion + 1;
      setCurrentQuestion(nextQuestion);
      setSelectedAnswer(null);
      setShowExplanation(false);
      saveProgress(nextQuestion + 1);
    } else {
      saveProgress(quizQuestions.length, true);
      setShowCompletion(true);
    }
  };

  const handleBack = () => {
    if (currentQuestion > 0) {
      const prevQuestion = currentQuestion - 1;
      setCurrentQuestion(prevQuestion);
      setSelectedAnswer(responses[prevQuestion] || null);
      setShowExplanation(responses[prevQuestion] !== undefined);
    }
  };

  const correctAnswers = Object.values(responses).filter((answer, index) =>
    answer === quizQuestions[index].correctAnswer
  ).length;

  if (isLoading) {
    return (
      <div className="module-form" style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh'
      }}>
        <p>Loading quiz...</p>
      </div>
    );
  }

  if (showCompletion) {
    return (
      <div className="quiz-completion-section">
        <h2 className="completion-title">
          <span>Congratulations!</span>
        </h2>
        <p className="completion-subtitle">
          You've successfully completed the Module 4 quiz!
        </p>
        <div className="completion-card">
          <div className="achievement-section">
            <h3 className="section-title">
              <span>Key Achievements</span>
            </h3>
            <p className="achievement-intro">You've demonstrated a great understanding of:</p>
            <ul className="achievement-list">
              <li>
                <span className="check"></span>
                <span>Effective communication techniques</span>
              </li>
              <li>
                <span className="check"></span>
                <span>Active listening and empathy skills</span>
              </li>
              <li>
                <span className="check"></span>
                <span>Strategies for building stronger relationships</span>
              </li>
            </ul>
          </div>
          <div className="next-steps-section">
            <h3 className="section-title">
              <span>Next Steps</span>
            </h3>
            <p>Continue your socio-relational wellness journey by applying these skills in daily life!</p>
            <p>Ready to learn how to integrate socio-relational well-being into your routine?</p>
          </div>
          <button
            className="resource-button"
            onClick={() => navigate('/panel/learn/socio-relational-resources')}
          >
            Return to Socio-Relational Resources
          </button>
        </div>
      </div>
    );
  }

  const currentQuizQuestion = quizQuestions[currentQuestion];

  const progress = ((currentQuestion + 1) / quizQuestions.length) * 100;
  const currentQuiz = quizQuestions[currentQuestion];

  return (
    <div className="module-form">
      <div className="module-container">
        <div className="module-left socio-relational-panel">
          <div className="module-header">
            <h1>Module 4 Quiz</h1>
            <p className="module-subtitle">Social Support Networks</p>
            <hr className="module-divider" />
            <p className="module-tool-info">
              Let's review what we've learned in a stress-free way.
              There's no scoring - this is just to reinforce the main ideas!
            </p>
          </div>
        </div>
        <div className="assessment-right-panel">
          <div className="assessment-content">
            <div className="module-progress">
              <div className="progress-bar">
                <div className="progress-fill" style={{ width: `${progress}%` }}></div>
              </div>
              <span className="progress-text">
                Question {currentQuestion + 1} of {quizQuestions.length}
              </span>
            </div>
            <div className="question-section">
              <h2 className="question-title">
                {currentQuiz.question}
              </h2>
              <p className="question-instruction">
                Please select the answer that best matches your understanding.
              </p>
              <div className="answer-options">
                {currentQuiz.options.map((option, index) => (
                  <label key={index} className="answer-option">
                    <input
                      type="radio"
                      name={`question-${currentQuestion}`}
                      value={index}
                      checked={selectedAnswer === index}
                      onChange={() => handleAnswerSelect(index)}
                      disabled={selectedAnswer !== null}
                    />
                    <span className="option-text">{option}</span>
                  </label>
                ))}
              </div>
              {showExplanation && (
                <div className="explanation">
                  <h3>{selectedAnswer === currentQuiz.correctAnswer ? 'Correct!' : 'Remember:'}</h3>
                  <p>{currentQuiz.explanation}</p>
                </div>
              )}
            </div>
            <div className="navigation-buttons">
              <button
                className="btn-secondary"
                onClick={currentQuestion === 0 ? () => navigate('/panel/learn/socio-relational-resources') : handleBack}
              >
                {currentQuestion === 0 ? 'Exit Quiz' : 'Previous Question'}
              </button>
              {currentQuestion < quizQuestions.length - 1 ? (
                <button
                  className="btn-primary"
                  onClick={handleNext}
                  disabled={selectedAnswer === null}
                >
                  Next Question
                </button>
              ) : (
                <button
                  className="btn-primary"
                  onClick={handleNext}
                  disabled={selectedAnswer === null}
                >
                  Complete Quiz
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SocioRelationalModule4QuizPage;

